import { platformStorageDownloadAuthorizationSchema, platformStorageSha256Schema, platformStorageUploadIntentIdSchema, platformStorageUploadTransportSchema, } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { projectsProjectIdSchema, projectsTenantIdSchema } from './projects-project.contract.js';
export const projectsProjectFileIdSchema = z.string().uuid();
export const projectsProjectFilePhaseSchema = z.enum(['before', 'during', 'after', 'other']);
export const projectsProjectFileCategorySchema = z.enum([
    'photo',
    'video',
    'document',
    'other',
]);
export const projectsProjectFileStateSchema = z.enum([
    'pending',
    'attached',
    'quarantined',
    'failed',
    'deleted',
]);
export const projectsProjectFolderIdSchema = z.string().uuid();
export const projectsProjectFolderNameSchema = z.string().trim().min(1).max(240);
export const projectsProjectFolderPathSchema = z.string().trim().min(1).max(2_000);
export const projectsProjectFileAllowedContentTypes = [
    'image/heic',
    'image/heif',
    'image/jpeg',
    'image/png',
    'image/webp',
    'video/mp4',
    'video/quicktime',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/json',
    'application/x-apple-arworldmap',
    'application/x-crest-detail-mesh',
    'application/x-crest-gaussian-splat',
    'application/x-crest-splat-keyframes',
    'model/vnd.usdz+zip',
];
export const projectsProjectFileContentTypeSchema = z.enum(projectsProjectFileAllowedContentTypes);
export const projectsProjectFileNameSchema = z.string().trim().min(1).max(240).superRefine((value, context) => {
    if (value === '.' || value === '..' || /[\\/\u0000-\u001f\u007f]/.test(value)) {
        context.addIssue({ code: 'custom', message: 'Project file name is not safe.' });
    }
});
export function projectsProjectFileCategoryForContentType(contentType) {
    if (contentType.startsWith('image/'))
        return 'photo';
    if (contentType.startsWith('video/'))
        return 'video';
    if (contentType === 'application/pdf' ||
        contentType === 'text/plain' ||
        contentType === 'text/csv' ||
        contentType.startsWith('application/msword') ||
        contentType.startsWith('application/vnd.'))
        return 'document';
    return 'other';
}
export const projectsProjectFileSchema = z.strictObject({
    id: projectsProjectFileIdSchema,
    tenantId: projectsTenantIdSchema,
    projectId: projectsProjectIdSchema,
    folderId: projectsProjectFolderIdSchema.nullable(),
    originalFileName: projectsProjectFileNameSchema,
    category: projectsProjectFileCategorySchema,
    contentType: projectsProjectFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    state: projectsProjectFileStateSchema,
    roomLabel: z.string().trim().min(1).max(120).nullable(),
    phase: projectsProjectFilePhaseSchema.nullable(),
    capturedAt: z.string().datetime({ offset: true }).nullable(),
    capturedLatitude: z.number().min(-90).max(90).nullable(),
    capturedLongitude: z.number().min(-180).max(180).nullable(),
    capturedAccuracyMeters: z.number().nonnegative().max(100_000).nullable(),
    uploadedByMembershipId: z.string().uuid(),
    revision: z.number().int().positive(),
    deletedAt: z.string().datetime({ offset: true }).nullable(),
    purgeAfter: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const projectsProjectFolderSchema = z.strictObject({
    id: projectsProjectFolderIdSchema,
    tenantId: projectsTenantIdSchema,
    projectId: projectsProjectIdSchema,
    parentFolderId: projectsProjectFolderIdSchema.nullable(),
    name: projectsProjectFolderNameSchema,
    path: projectsProjectFolderPathSchema,
    isSystem: z.boolean(),
    createdByMembershipId: z.string().uuid(),
    revision: z.number().int().positive(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const projectsProjectFolderListResponseSchema = z.strictObject({
    data: z.array(projectsProjectFolderSchema),
});
export const projectsProjectFileListQuerySchema = z.strictObject({
    limit: z.number().int().min(1).max(200).default(100),
    offset: z.number().int().min(0).max(1_000_000).default(0),
    state: z.enum(['active', 'deleted', 'all']).default('active'),
});
export const projectsProjectFileListResponseSchema = z.strictObject({
    data: z.array(projectsProjectFileSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
export const projectsInitiateProjectFileUploadSchema = z.strictObject({
    idempotencyKey: z.string().uuid(),
    originalFileName: projectsProjectFileNameSchema,
    contentType: projectsProjectFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(5 * 1_024 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
    roomLabel: z.string().trim().min(1).max(120).nullable().default(null),
    phase: projectsProjectFilePhaseSchema.nullable().default(null),
    capturedAt: z.string().datetime({ offset: true }).nullable().default(null),
});
export const projectsProjectFileUploadAuthorizationResponseSchema = z.strictObject({
    data: z.strictObject({
        file: projectsProjectFileSchema,
        uploadIntentId: platformStorageUploadIntentIdSchema,
        transport: platformStorageUploadTransportSchema,
    }),
});
export const projectsFinalizeProjectFileUploadSchema = z.strictObject({
    uploadIntentId: platformStorageUploadIntentIdSchema,
});
export const projectsProjectFileResponseSchema = z.strictObject({
    data: projectsProjectFileSchema,
});
export const projectsProjectFileDownloadResponseSchema = z.strictObject({
    data: platformStorageDownloadAuthorizationSchema,
});
export const projectsProjectFileMutationSchema = z.strictObject({
    expectedRevision: z.number().int().positive(),
});
//# sourceMappingURL=projects-project-file.contract.js.map